import { enhanceGLBridge } from '@meitu/wasm-lib';
import { Wasm } from '@/editor/extensions/wasm';
import { getFramework, IFramework } from '@/framework';
import { ILayerProps } from '@/version-types';
import { getImageEnhanceState, IState, IStore, watchImageEnhanceChange } from '../states/image-enhance-state';
import {
    IBaseEnhance,
    IColorEnhance,
    IHslEnhance,
    ILightEnhance,
    IParticularsEnhance,
    IPosterizeEnhance,
} from '../types';
import { formatExtractColors } from '../utils';
import { BasicImageEnhance } from './basic-image-enhance';
import { MakeupImageEnhance } from './makeup-image-enhance';

export class ImageEnhanceController {
    /** 编辑器实例 */
    private framework: IFramework;
    private store: IStore;
    public basicEnhance: BasicImageEnhance;
    public makeupEnhance: MakeupImageEnhance;

    constructor(identity: string) {
        this.framework = getFramework(identity);
        this.store = getImageEnhanceState(identity);

        watchImageEnhanceChange(identity, (state) => {
            this.store = state;
        });

        this.basicEnhance = new BasicImageEnhance(identity);
        this.makeupEnhance = new MakeupImageEnhance(identity);
    }

    getState() {
        return this.store as IState;
    }

    async openBasic(mogType: string, isBatchEditor?: boolean) {
        const originIndex = this.framework.service.frame.getActiveFrameId();
        const originLayer = this.framework.service.frame.getMainLayer(originIndex);
        if (!originLayer) return;
        this.framework.service.mask.show({
            loading: true,
        });
        // ATTENTION: 强制先等待 wasm 初始化完成是因为 useInvokeFunc 可能先于 wasm.init 完成，导致报错
        await this.framework.service.registration.getInstance<Wasm>('Wasm').whenReady();
        await this.basicEnhance.create(originLayer, mogType, isBatchEditor);
        this.framework.service.mask.hide();
        this.extractColors();
    }

    async closeBasic() {
        this.framework.service.mask.show({ loading: true });
        let hasEnhance: boolean | undefined = undefined;
        if (this.store.isBatchEditor) {
            hasEnhance = await this.basicEnhance.batchApplyConfirm();
        } else {
            hasEnhance = await this.basicEnhance.applyConfirm();
        }
        this.framework.service.mask.hide();
        return hasEnhance;
    }

    async openMakeup() {
        const originIndex = this.framework.service.frame.getActiveFrameId();
        const originLayer = this.framework.service.frame.getMainLayer(originIndex);

        if (!originLayer) return;

        this.framework.service.mask.show({ loading: true });
        // ATTENTION: 强制先等待 wasm 初始化完成是因为 useInvokeFunc 可能先于 wasm.init 完成，导致报错
        await this.framework.service.registration.getInstance<Wasm>('Wasm').whenReady();
        await this.makeupEnhance.create(originLayer);
        this.framework.service.mask.hide();
        this.extractColors();
    }

    async closeMakeup() {
        this.framework.service.mask.show({ loading: true });
        this.store.resetEnhance();
        this.framework.service.mask.hide();
    }

    reset() {
        this.store.destroy();
    }

    setOriginLayer(layer: ILayerProps | null) {
        this.store.setOriginLayer(layer);
    }

    setOriginLayers(layers: ILayerProps[]) {
        this.store.setOriginLayers(layers);
    }

    setLastOriginLayers(layers: ILayerProps[]) {
        this.store.setLastOriginLayers(layers);
    }

    setBaseEnhance(baseEnhance: IBaseEnhance) {
        this.store.setBaseEnhance(baseEnhance);
    }

    setColorEnhance(colorEnhance: IColorEnhance) {
        this.store.setColorEnhance(colorEnhance);
    }

    setLightEnhance(lightEnhance: ILightEnhance) {
        this.store.setLightEnhance(lightEnhance);
    }

    setParticularsEnhance(particularsEnhance: IParticularsEnhance) {
        this.store.setParticularsEnhance(particularsEnhance);
    }

    setPosterizeEnhance(posterizeEnhance: IPosterizeEnhance) {
        this.store.setPosterizeEnhance(posterizeEnhance);
    }

    setHslEnhance(hslEnhance: IHslEnhance) {
        this.store.setHslEnhance(hslEnhance);
    }

    async extractColors() {
        const store = getImageEnhanceState(this.framework.identity);
        const colors = await enhanceGLBridge.imageDetec();

        store.setExtractColors(formatExtractColors(colors));
    }

    watch(callback: Parameters<typeof watchImageEnhanceChange>['1']) {
        return watchImageEnhanceChange(this.framework.identity, callback);
    }

    setIsBatchEditor(isBatchEditor: boolean) {
        this.store.setIsBatchEditor(isBatchEditor);
    }
}
